/**
 * ReferralPatientModal — muestra los datos primordiales del paciente
 * referido (desde patient_snapshot) SIN acceder al expediente completo.
 * Permite al doctor receptor solicitar información adicional al emisor.
 */
import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { X, MessageSquare, Send, Stethoscope } from "@/components/icons";
import Portal from "./Portal";
import { doctorLabel } from "../lib/doctorLabel";

function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
}

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

export default function ReferralPatientModal({ referral, onClose, onSaved }) {
  const s = referral.patient_snapshot || {};
  const age = calcAge(s.date_of_birth);
  const [showRequest, setShowRequest] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [thread, setThread] = useState(referral.info_thread || []);

  useEffect(() => {
    // Marcar como leído automáticamente al abrir
    if ((referral.unread_for_receiver || 0) > 0) {
      api.post(`/referrals/${referral.id}/mark-thread-read`).catch(() => {});
    }
  }, [referral.id, referral.unread_for_receiver]);

  const sendRequest = async () => {
    if (msg.trim().length < 3) {
      toast.error("Escribe qué información necesitas (mínimo 3 caracteres)");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(
        `/referrals/${referral.id}/request-info`,
        { message: msg.trim() }
      );
      toast.success(
        `Solicitud enviada a ${doctorLabel(referral.from_doctor_name || "el doctor")}`
      );
      if (data?.entry) setThread((t) => [...t, data.entry]);
      setMsg("");
      setShowRequest(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const items = [
    { label: "Nombre", value: fmt(s.full_name || referral.patient_name) },
    {
      label: "Edad",
      value: age !== null ? `${age} años` : fmt(s.date_of_birth),
    },
    { label: "Sexo", value: fmt(s.gender) },
    { label: "Cédula / Documento", value: fmt(s.document_id) },
    { label: "Teléfono", value: fmt(s.phone) },
    { label: "Email", value: fmt(s.email) },
    { label: "Tipo de sangre", value: fmt(s.blood_type) },
    { label: "Alergias", value: fmt(s.allergies) },
    {
      label: "Condiciones crónicas",
      value: fmt(s.chronic_conditions),
    },
    {
      label: "Medicación actual",
      value: fmt(s.current_medications),
    },
    { label: "Dirección", value: fmt(s.address), full: true },
  ];

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-[#171717] rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="referral-patient-modal"
        >
          <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-black/[0.06] dark:border-white/[0.08]">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40">
                Paciente referido
              </div>
              <h2 className="text-xl font-extrabold text-black dark:text-white mt-0.5">
                {s.full_name || referral.patient_name || "Paciente"}
              </h2>
              <p className="text-xs text-black/60 dark:text-white/60 mt-1">
                Enviado por {doctorLabel(referral.from_doctor_name)}
                {referral.from_doctor_specialty
                  ? ` · ${referral.from_doctor_specialty}`
                  : ""}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl hover:bg-black/[0.06] dark:hover:bg-white/[0.08] flex items-center justify-center text-black/60 dark:text-white/60"
              data-testid="ref-patient-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Motivo clínico */}
            <div className="bg-blue-50 dark:bg-[#01BC8E]/10 rounded-2xl p-4 border border-blue-100 dark:border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope className="w-4 h-4 text-[#019A75] dark:text-emerald-400" />
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#019A75] dark:text-emerald-400">
                  Motivo del referido
                </div>
              </div>
              <div className="text-sm text-black dark:text-white font-semibold">
                {referral.reason}
              </div>
              {referral.clinical_notes && (
                <div className="mt-2 text-sm text-black/80 dark:text-white/80 whitespace-pre-line">
                  {referral.clinical_notes}
                </div>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                <span
                  className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${
                    referral.urgency === "urgente"
                      ? "bg-red-100 text-red-700"
                      : "bg-black/[0.06] dark:bg-white/[0.10] text-black/70 dark:text-white/70"
                  }`}
                >
                  {referral.urgency === "urgente" ? "Urgente" : "Rutina"}
                </span>
              </div>
            </div>

            {/* Datos primordiales */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40 mb-2">
                Datos primordiales
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {items.map((it, i) => (
                  <div
                    key={i}
                    className={`bg-black/[0.03] dark:bg-white/[0.04] rounded-xl px-4 py-2.5 ${
                      it.full ? "sm:col-span-2" : ""
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-black/50 dark:text-white/40">
                      {it.label}
                    </div>
                    <div className="text-sm text-black dark:text-white font-semibold mt-0.5 break-words">
                      {it.value}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-black/50 dark:text-white/50 mt-3">
                Esta información fue capturada al momento de crear el referido.
                Para ver el expediente completo, contacta al doctor emisor.
              </p>
            </div>

            {/* Hilo de conversación */}
            {thread.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40 mb-2">
                  Solicitudes e información compartida
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {thread.map((m) => {
                    const isReceiver = m.from === "receiver";
                    return (
                      <div
                        key={m.id}
                        className={`p-3 rounded-2xl text-sm ${
                          isReceiver
                            ? "bg-amber-50 dark:bg-amber-500/10 text-black dark:text-white"
                            : "bg-blue-50 dark:bg-[#01BC8E]/10 text-black dark:text-white"
                        }`}
                      >
                        <div className="text-[10px] uppercase tracking-wider font-bold text-black/50 dark:text-white/50 mb-1">
                          {isReceiver
                            ? `Solicitud · ${doctorLabel(m.from_doctor_name || "")}`
                            : `Respuesta · ${doctorLabel(m.from_doctor_name || "")}`}
                          {" · "}
                          {new Date(m.created_at).toLocaleString("es-DO", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        <div className="whitespace-pre-line">{m.message}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Solicitar información adicional */}
            {referral.from_doctor_id && (
              <div className="border-t border-black/[0.06] dark:border-white/[0.08] pt-5">
                {!showRequest ? (
                  <button
                    onClick={() => setShowRequest(true)}
                    data-testid="ref-request-info-btn"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.10] text-black dark:text-white text-sm font-bold transition"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Solicitar información adicional al{" "}
                    {doctorLabel(referral.from_doctor_name)}
                  </button>
                ) : (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-black/50 dark:text-white/40 mb-2">
                      Solicitar información adicional
                    </div>
                    <textarea
                      value={msg}
                      onChange={(e) => setMsg(e.target.value)}
                      rows={3}
                      placeholder="Ej: ¿Podrías enviarme los últimos estudios de laboratorio y rayos X del paciente?"
                      data-testid="ref-request-textarea"
                      className="w-full px-4 py-2.5 rounded-xl border border-black/[0.10] dark:border-white/[0.10] bg-white dark:bg-[#0d0d0d] text-sm text-black dark:text-white placeholder-black/40 dark:placeholder-white/40 resize-none"
                    />
                    <div className="flex items-center justify-end gap-2 mt-3">
                      <button
                        onClick={() => {
                          setShowRequest(false);
                          setMsg("");
                        }}
                        className="px-4 py-2 rounded-xl text-sm font-bold text-black/60 dark:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={sendRequest}
                        disabled={sending}
                        data-testid="ref-request-send"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#01BC8E] hover:bg-[#019A75] text-white text-sm font-bold shadow-lg shadow-emerald-500/30 disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5" />
                        {sending ? "Enviando…" : "Enviar solicitud"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
